import { Link } from 'react-router-dom';
import { ArrowRight, Check, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import Footer from '../../components/Footer';

const Shelving = () => {
  const [showGallery, setShowGallery] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  const galleryImages = [
    '/gallery/shelving/shelving-metal-workbench.webp',
    '/gallery/shelving/shelving-industrial-room.webp',
    '/images/products/shelving.webp',
    '/images/products/shelving42.webp',
    '/images/products/warehouse.webp',
  ];

  const features = [
    'Bolted or boltless options available',
    'Steel, chrome, or plastic finishes',
    'Ideal for archives, parts, and retail',
    'Mobile shelving systems available',
    'Custom sizing for any space',
    'Easy assembly and reconfiguration',
    'Load capacity up to 500kg per shelf',
  ];

  const applications = [
    'Office Archives',
    'Retail Storage',
    'Workshop Storage',
    'Medical & Pharmaceutical',
    'Warehouse Picking',
  ];

  return (
    <div className="pt-20">
      <section className="w-full bg-charcoal py-16 lg:py-24">
        <div className="px-6 lg:px-[7vw]">
          <Link to="/products" className="text-white/60 hover:text-white text-sm mb-4 inline-block">
            ← Back to Products
          </Link>
          <h1 className="font-display font-bold text-white text-4xl lg:text-6xl mb-4">
            SHELVING THAT FITS.
          </h1>
          <p className="text-white/60 max-w-2xl text-lg">
            Heavy-duty bays for warehouses, archives, retail backrooms and workshops.
          </p>
        </div>
      </section>

      <section className="w-full bg-offwhite py-12 lg:py-20">
        <div className="px-6 lg:px-[7vw]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left - Image */}
            <div>
              <div 
                className="aspect-video rounded-2xl overflow-hidden mb-4 cursor-pointer"
                onClick={() => galleryImages.length > 0 && setShowGallery(true)}
              >
                <img 
                  src="/shelving_bay.jpg" 
                  alt="Industrial Shelving" 
                  width="800" 
                  height="450" 
                  loading="eager" 
                  decoding="async" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform" 
                />
              </div>
              {galleryImages.length > 0 && (
                <p className="text-center text-charcoal/50 text-sm">
                  Click image to view gallery ({galleryImages.length} photos)
                </p>
              )}
            </div>

            {/* Right - Content */}
            <div>
              <h2 className="font-display font-bold text-charcoal text-2xl lg:text-3xl mb-4">
                Industrial Shelving
              </h2>
              <p className="text-charcoal/70 mb-6 leading-relaxed">
                Heavy-duty bays for warehouses, archives, retail backrooms and workshops—adjustable, 
                modular, built to last. From light-duty office shelving to heavy-duty industrial systems.
              </p>

              <h3 className="font-display font-bold text-charcoal text-lg mb-3">Key Features</h3>
              <ul className="space-y-2 mb-6">
                {features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-charcoal/80">
                    <Check className="w-4 h-4 text-lime flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-display font-bold text-charcoal text-lg mb-3">Applications</h3>
              <div className="flex flex-wrap gap-2 mb-8">
                {applications.map((app, idx) => (
                  <span key={idx} className="bg-charcoal/5 text-charcoal/70 px-3 py-1 rounded-full text-sm">{app}</span>
                ))}
              </div>

              <div className="flex gap-4 flex-wrap">
                <Link to="/contact" className="bg-lime text-charcoal px-6 py-3 rounded-lg font-medium inline-flex items-center gap-2 hover:bg-lime/90 transition-colors">
                  Request a Quote
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a href="tel:+27829713299" className="bg-charcoal text-white px-6 py-3 rounded-lg font-medium inline-flex items-center gap-2 hover:bg-charcoal/80 transition-colors">
                  <Phone className="w-4 h-4" />
                  Call Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <section className="w-full bg-white py-12 lg:py-16">
        <div className="px-6 lg:px-[7vw]">
          <h3 className="font-display font-bold text-charcoal text-xl mb-6">Related Products</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/products/racking" className="group">
              <div className="aspect-video rounded-xl overflow-hidden mb-3">
                <img src="/racking_aisle.jpg" alt="Racking" width="400" height="225" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              </div>
              <h4 className="font-display font-bold text-charcoal">Pallet Racking</h4>
            </Link>
            <Link to="/products/mezzanine" className="group">
              <div className="aspect-video rounded-xl overflow-hidden mb-3">
                <img src="/mezzanine_install.jpg" alt="Mezzanine" width="400" height="225" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              </div>
              <h4 className="font-display font-bold text-charcoal">Mezzanine Floors</h4>
            </Link>
            <Link to="/products/storage" className="group">
              <div className="aspect-video rounded-xl overflow-hidden mb-3">
                <img src="/lockers_storage.jpg" alt="Storage" width="400" height="225" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              </div>
              <h4 className="font-display font-bold text-charcoal">Lockers & Storage</h4>
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery Modal */}
      {showGallery && galleryImages.length > 0 && (
        <div className="fixed inset-0 z-[200] bg-charcoal/95 flex items-center justify-center p-4">
          <button
            onClick={() => setShowGallery(false)}
            aria-label="Close gallery"
            className="absolute top-4 right-4 text-white w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
          >
            ✕
          </button>
          <div className="max-w-4xl max-h-[80vh]">
            <img
              src={galleryImages[currentImage]}
              alt={`Gallery ${currentImage + 1}`}
              width="1200"
              height="675"
              loading="eager"
              decoding="async"
              className="w-full h-full object-contain max-h-[80vh]"
            />
            <div className="flex justify-center gap-4 mt-4">
              <button
                onClick={() => setCurrentImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
                aria-label="Previous image"
                className="text-white bg-white/10 p-2 rounded"
              >
                <ChevronLeft />
              </button>
              <span className="flex items-center text-white">
                {currentImage + 1} / {galleryImages.length}
              </span>
              <button
                onClick={() => setCurrentImage((prev) => (prev + 1) % galleryImages.length)}
                aria-label="Next image"
                className="text-white bg-white/10 p-2 rounded"
              >
                <ChevronRight />
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Shelving;
